import React, { useRef, useState } from 'react';
import Footer from '../components/Footer';
import './About.css';

const W = 'https://static.wixstatic.com/media/';

/* Every team member with their exact Wix photo URL */
const TEAM_LEADERS = [
  {
    name: 'Iris Cao',
    role: 'Co-founder & Chief Executive Officer',
    initials: 'IC',
    is_founder: true,
    photo: W + 'fb795d_f14776069bef4fc99db62bb991469444~mv2.jpg',
    story: 'Co-founder of Echo and long-time participant in the Novi Band Program, Iris is passionate about democratizing music education for all.',
  },
  {
    name: 'Catherine Hu',
    role: 'Co-founder & Chief Operating Officer',
    initials: 'CH',
    is_founder: true,
    photo: W + 'fb795d_b4eb54eeeccf4da1b25171d7bf1398a8~mv2.jpg',
    story: 'Catherine brings her passion for music and community-building to every aspect of ECHO\'s operations and global growth.',
  },
  {
    name: 'Lewen Feng',
    role: 'Chief Financial Officer',
    initials: 'LF',
    is_founder: false,
    photo: W + 'fb795d_eb9358ca6abd48228d8775c9b6a47da4~mv2.png',
  },
  {
    name: 'Pratyay Rao',
    role: 'Chief Technology Officer',
    initials: 'PR',
    is_founder: false,
    photo: W + 'ce44e1_7b8b510416554703b8bddcf448b729ff~mv2.jpg',
  },
  {
    name: 'Jonathan Yu',
    role: 'Chapter Director & Masterclass Host',
    initials: 'JY',
    is_founder: false,
    photo: W + 'fb795d_d150d5e635c747cd933bc6790380b42f~mv2.png',
  },
  {
    name: 'Maria Lapinski',
    role: 'Marketing Director',
    initials: 'ML',
    is_founder: false,
    photo: W + 'fb795d_b8c22e2e19aa42c5a6ef3abbeb44382f~mv2.png',
  },
  {
    name: 'Raj Narayan',
    role: 'Volunteer Director',
    initials: 'RN',
    is_founder: false,
    photo: W + 'fb795d_badfd2d0ca084e759d3d77f63866a447~mv2.png',
  },
  {
    name: 'Ming Hsuan Ma',
    role: 'Volunteer Director',
    initials: 'MM',
    is_founder: false,
    photo: W + 'fb795d_5f768c78745d48159d824d46d9d18fab~mv2.jpg',
  },
  {
    name: 'Evan Ong',
    role: 'Outreach Director',
    initials: 'EO',
    is_founder: false,
    photo: W + 'fb795d_b8431ff828dc470d91e642b70e625846~mv2.png',
  },
  {
    name: 'Vivian Tang',
    role: 'Outreach Director',
    initials: 'VT',
    is_founder: false,
    photo: W + 'fb795d_64c7a96ab4c04ce382bf838815579ebd~mv2.png',
  },
];

const ASSOCIATES = [
  {
    name: 'Kodi Qin',
    role: 'Outreach Associate',
    initials: 'KQ',
    photo: W + 'fb795d_5f768c78745d48159d824d46d9d18fab~mv2.jpg',
  },
  {
    name: 'Jaynie Jiang',
    role: 'Creative Associate',
    initials: 'JJ',
    photo: W + 'fb795d_078e365a506e44f987c7dbe6e7e5589d~mv2.png',
  },
  {
    name: 'Anika Anand',
    role: 'Creative Associate',
    initials: 'AA',
    photo: W + 'fb795d_fae2468260404ad0ab39f2d83d00887b~mv2.png',
  },
];

function FadeUp({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  React.useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref}
      className={`fade-up ${visible ? 'fade-up--visible' : ''} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

function MemberCard({ member, size = 'large' }) {
  const [imgError, setImgError] = useState(false);
  const isStory = size === 'story';
  return (
    <div className={`member-card member-card--${size}`}>
      <div className={isStory ? 'member-photo-wrap member-photo-wrap--story' : 'member-photo-wrap'}>
        {!imgError ? (
          <img
            src={member.photo}
            alt={member.name}
            className="member-photo"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="member-photo-fallback">{member.initials}</div>
        )}
        {member.is_founder && !isStory && <div className="member-founder-ribbon">Co-founder</div>}
      </div>
      <div className="member-info">
        {member.is_founder && isStory && (
          <span className="member-founder-tag">Co-founder</span>
        )}
        <div className="member-name">{member.name}</div>
        <div className="member-role">{member.role}</div>
        {member.story && <p className="member-bio">{member.story}</p>}
      </div>
    </div>
  );
}

export default function About({ navigate }) {
  return (
    <div className="about-page">

      {/* ── HERO ── */}
      <div className="about-hero">
        <div className="about-hero-bg">
          <img src={W + 'fb795d_5f768c78745d48159d824d46d9d18fab~mv2.jpg'} alt="" className="about-hero-bgimg" />
          <div className="about-hero-overlay" />
        </div>
        <div className="about-hero-content">
          <div className="page-hero-ghost">About</div>
          <div className="section-tag">Who We Are</div>
          <h1>About <em>ECHO</em></h1>
          <p>A youth-led, global organization providing a stage for musicians of all ages, giving every musician a chance to be heard.</p>
        </div>
      </div>

      {/* ── STORY + FOUNDERS ── */}
      <section className="story-section">
        <FadeUp className="story-left">
          <div className="section-tag">Our Story</div>
          <h2 className="section-h2">How it all <em>began</em></h2>
          <blockquote className="story-quote">
            "Music washes away from the soul the dust of everyday life."
            <cite>- Berthold Auerbach</cite>
          </blockquote>
          <p>Echo first began as a small passion project created by two high schoolers in Novi, MI - Iris Cao and Catherine Hu. As long-time music lovers and participants in the Novi Band Program, they experienced the joy that music brings and wanted to share that with more people.</p>
          <p>In August 2024, Echo was officially launched as a youth organization dedicated to promoting musical passion. Since then, they have built a community of passionate high school tutors from all corners of the globe, practicing many different styles and instruments all hoping to make a real difference.</p>
        </FadeUp>

        <div className="story-founders">
          {TEAM_LEADERS.filter(m => m.is_founder).map((m, i) => (
            <FadeUp key={i} delay={100 + i * 80}>
              <MemberCard member={m} size="story" />
            </FadeUp>
          ))}
        </div>
      </section>

      {/* ── MISSION HIGHLIGHT ── */}
      <section className="mission-highlight">
        <FadeUp className="mission-highlight-inner">
          <div className="section-tag">Our Mission</div>
          <h2 className="section-h2">
            Amplifying <em>musical passion</em><br />for all backgrounds
          </h2>
          <p>We are an international initiative passionate about bringing music to students everywhere. We aim to use our online platform to bring music education to students who need it most and removing barriers of geography, money, and experience.</p>
        </FadeUp>
      </section>

      {/* ── LEADERSHIP TEAM ── */}
      <section className="team-section">
        <FadeUp>
          <div className="section-tag">Leadership</div>
          <h2 className="section-h2">Meet the people <em>behind ECHO</em></h2>
          <p className="team-intro">Our team consists of high school students who are passionate about music. Each brings unique skills to grow ECHO's global impact.</p>
        </FadeUp>
        <div className="team-grid">
          {TEAM_LEADERS.map((m, i) => (
            <FadeUp key={i} delay={i * 60}>
              <MemberCard member={m} size="large" />
            </FadeUp>
          ))}
          {/* Join card */}
          <FadeUp delay={TEAM_LEADERS.length * 60}>
            <div className="member-card member-card--join">
              <div className="member-photo-wrap member-join-photo-wrap">
                <div className="member-join-icon">+</div>
              </div>
              <div className="member-info">
                <div className="member-name" style={{ color: 'var(--cream)' }}>You?</div>
                <div className="member-role">Open Positions</div>
                <button
                  className="btn-join"
                  onClick={() => navigate('join')}
                >
                  Apply Now →
                </button>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ── ASSOCIATES ── */}
      <section className="associates-section">
        <FadeUp>
          <div className="section-tag">Associates</div>
          <h2 className="section-h2">Our <em>incredible</em> associates</h2>
        </FadeUp>
        <div className="associates-grid">
          {ASSOCIATES.map((m, i) => (
            <FadeUp key={i} delay={i * 80}>
              <MemberCard member={m} size="small" />
            </FadeUp>
          ))}
        </div>
      </section>

      <Footer navigate={navigate} />
    </div>
  );
}
